import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { pharmacies, PHARMACY_SERVICES, type Pharmacy } from '@/mocks/pharmacies';
import PharmacyGallery from '../components/PharmacyGallery';
import PharmacyReviews from '../components/PharmacyReviews';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';

type Tab = 'info' | 'schedule' | 'reviews' | 'services';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const DAY_MAP: Record<string, string> = {
  'Пн': 'Monday', 'Вт': 'Tuesday', 'Ср': 'Wednesday',
  'Чт': 'Thursday', 'Пт': 'Friday', 'Сб': 'Saturday', 'Вс': 'Sunday',
};

function buildOpeningHoursSpec(pharmacy: Pharmacy) {
  if (pharmacy.is24h) {
    return [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        opens: '00:00',
        closes: '23:59',
      },
    ];
  }
  return pharmacy.schedule.map(s => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: DAY_MAP[s.day] ?? s.day,
    opens: s.open,
    closes: s.close === '24:00' ? '23:59' : s.close,
  }));
}

function buildAmenityFeatures(pharmacy: Pharmacy) {
  return PHARMACY_SERVICES
    .filter(svc => pharmacy.services.includes(svc.id))
    .map(svc => ({
      '@type': 'LocationFeatureSpecification',
      name: svc.label,
      value: true,
    }));
}

function buildReviews(pharmacy: Pharmacy) {
  return pharmacy.reviewsList.slice(0, 5).map(r => ({
    '@type': 'Review',
    author: { '@type': 'Person', name: r.author },
    datePublished: r.date,
    reviewBody: r.text,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: r.rating,
      bestRating: 5,
      worstRating: 1,
    },
  }));
}

function buildLocalBusinessSchema(pharmacy: Pharmacy) {
  return {
    '@context': 'https://schema.org',
    '@type': ['Pharmacy', 'LocalBusiness', 'MedicalOrganization'],
    '@id': `${SITE_URL}/pharmacies/${pharmacy.id}`,
    name: pharmacy.name,
    description: pharmacy.description,
    url: `${SITE_URL}/pharmacies/${pharmacy.id}`,
    telephone: pharmacy.phone,
    priceRange: '₽₽',
    currenciesAccepted: 'RUB',
    paymentAccepted: pharmacy.paymentMethods.join(', '),
    address: {
      '@type': 'PostalAddress',
      streetAddress: pharmacy.address,
      addressLocality: 'Москва',
      addressRegion: 'Москва',
      postalCode: '101000',
      addressCountry: 'RU',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: pharmacy.coords.lat,
      longitude: pharmacy.coords.lng,
    },
    hasMap: `https://maps.google.com/?q=${pharmacy.coords.lat},${pharmacy.coords.lng}`,
    openingHoursSpecification: buildOpeningHoursSpec(pharmacy),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: pharmacy.rating,
      reviewCount: pharmacy.reviews,
      bestRating: 5,
      worstRating: 1,
    },
    review: buildReviews(pharmacy),
    image: pharmacy.gallery.length > 0 ? pharmacy.gallery : [pharmacy.image],
    photo: pharmacy.gallery.map((img, i) => ({
      '@type': 'ImageObject',
      url: img,
      name: `${pharmacy.name} — фото ${i + 1}`,
    })),
    amenityFeature: buildAmenityFeatures(pharmacy),
    publicAccess: true,
    isAccessibleForFree: true,
    sameAs: [
      `https://maps.google.com/?q=${pharmacy.coords.lat},${pharmacy.coords.lng}`,
      `https://yandex.ru/maps/?ll=${pharmacy.coords.lng},${pharmacy.coords.lat}&z=17`,
    ],
    parentOrganization: {
      '@type': 'Organization',
      name: 'NeonPharma',
      url: SITE_URL,
    },
  };
}

function buildBreadcrumbSchema(pharmacy: Pharmacy) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Главная', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Аптеки', item: `${SITE_URL}/pharmacies` },
      { '@type': 'ListItem', position: 3, name: pharmacy.name, item: `${SITE_URL}/pharmacies/${pharmacy.id}` },
    ],
  };
}

function buildFaqSchema(pharmacy: Pharmacy) {
  const questions = [
    {
      q: `Как добраться до аптеки ${pharmacy.name}?`,
      a: pharmacy.howToGet,
    },
    {
      q: `Какой режим работы у аптеки ${pharmacy.name}?`,
      a: pharmacy.is24h
        ? `Аптека ${pharmacy.name} работает круглосуточно, 24 часа в сутки, 7 дней в неделю, без выходных.`
        : `Аптека ${pharmacy.name} работает ${pharmacy.hours}. Расписание по дням: ${pharmacy.schedule.map(s => `${s.day}: ${s.open}–${s.close}`).join(', ')}.`,
    },
    {
      q: `Какие услуги доступны в аптеке ${pharmacy.name}?`,
      a: `В аптеке ${pharmacy.name} доступны следующие услуги: ${PHARMACY_SERVICES.filter(s => pharmacy.services.includes(s.id)).map(s => s.label).join(', ')}.`,
    },
    {
      q: `Есть ли доставка из аптеки ${pharmacy.name}?`,
      a: pharmacy.hasDelivery
        ? `Да, аптека ${pharmacy.name} осуществляет доставку лекарств на дом.`
        : `Аптека ${pharmacy.name} не осуществляет доставку. Вы можете заказать доставку через сайт NeonPharma.`,
    },
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };
}

// ─── Component ────────────────────────────────────────────────────────────────
const PharmacyDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('info');
  const [copied, setCopied] = useState(false);

  const pharmacy: Pharmacy | undefined = pharmacies.find(p => p.id === Number(id));

  useEffect(() => {
    if (!pharmacy) {
      navigate('/pharmacies', { replace: true });
      return;
    }
    window.scrollTo(0, 0);
  }, [pharmacy, navigate]);

  if (!pharmacy) return null;

  const jsonLdSchemas = [
    buildLocalBusinessSchema(pharmacy),
    buildBreadcrumbSchema(pharmacy),
    buildFaqSchema(pharmacy),
  ];

  const nearby = pharmacy.nearbyPharmacies
    .map(nid => pharmacies.find(p => p.id === nid))
    .filter(Boolean) as Pharmacy[];

  const handleCopyPhone = () => {
    navigator.clipboard.writeText(pharmacy.phone).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'info', label: 'Информация', icon: 'ri-information-line' },
    { id: 'schedule', label: 'Расписание', icon: 'ri-time-line' },
    { id: 'services', label: 'Услуги', icon: 'ri-service-line' },
    { id: 'reviews', label: `Отзывы (${pharmacy.reviewsList.length})`, icon: 'ri-star-line' },
  ];

  // Structured opening hours string for display
  const openingHoursText = pharmacy.is24h
    ? 'Ежедневно 00:00–24:00'
    : pharmacy.hours;

  return (
    <div className="min-h-screen" style={{ background: '#050508' }}>
      <SeoHead
        title={`${pharmacy.name} — аптека ${pharmacy.metro}, ${pharmacy.district} | NeonPharma`}
        description={`${pharmacy.name}: ${pharmacy.address}. ${pharmacy.is24h ? 'Работает 24/7.' : pharmacy.hours + '.'} Рейтинг ${pharmacy.rating}/5 (${pharmacy.reviews} отзывов). Тел: ${pharmacy.phone}. Услуги: ${PHARMACY_SERVICES.filter(s => pharmacy.services.includes(s.id)).map(s => s.label).slice(0, 3).join(', ')}.`}
        keywords={`${pharmacy.name}, аптека ${pharmacy.metro}, аптека ${pharmacy.district}, NeonPharma Москва, аптека рядом`}
        canonical={`/pharmacies/${pharmacy.id}`}
        ogImage={pharmacy.gallery?.[0] ?? pharmacy.image}
        jsonLd={jsonLdSchemas}
      />
      <Navbar />

      {/* Hero banner */}
      <section className="relative pt-24 pb-0 overflow-hidden">
        <div className="absolute inset-0 h-64" style={{ background: 'linear-gradient(to bottom, rgba(255,45,120,0.06), transparent)' }} />
        <div className="absolute top-10 left-1/3 w-80 h-40 rounded-full pointer-events-none opacity-10" style={{ background: '#ff2d78', filter: 'blur(80px)' }} />
        <div className="absolute top-0 right-1/4 w-60 h-40 rounded-full pointer-events-none opacity-8" style={{ background: '#00f5ff', filter: 'blur(70px)' }} />

        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 relative z-10">
          {/* Breadcrumb */}
          <nav aria-label="Хлебные крошки" className="flex items-center gap-2 font-mono text-xs text-white/30 mb-6 flex-wrap">
            <Link to="/" className="hover:text-white/60 transition-colors">Главная</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/pharmacies" className="hover:text-white/60 transition-colors">Аптеки</Link>
            <i className="ri-arrow-right-s-line" />
            <span style={{ color: '#ff2d78' }}>{pharmacy.name}</span>
          </nav>

          {/* Title row */}
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 pb-8">
            <div className="flex-1">
              {/* Status + tags */}
              <div className="flex items-center gap-3 flex-wrap mb-3">
                <div
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full font-mono text-xs"
                  style={pharmacy.isOpen || pharmacy.is24h
                    ? { background: 'rgba(57,255,20,0.1)', border: '1px solid rgba(57,255,20,0.3)', color: '#39ff14' }
                    : { background: 'rgba(255,45,120,0.1)', border: '1px solid rgba(255,45,120,0.3)', color: '#ff2d78' }
                  }
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: pharmacy.isOpen || pharmacy.is24h ? '#39ff14' : '#ff2d78' }} />
                  {pharmacy.is24h ? 'Работает 24/7' : pharmacy.isOpen ? 'Открыто' : 'Закрыто'}
                </div>
                {pharmacy.tags.map(tag => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-full font-mono text-xs"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-orbitron font-black text-3xl md:text-4xl text-white mb-2 leading-tight">
                {pharmacy.name}
              </h1>
              <p className="font-mono text-sm text-white/50 flex items-center gap-2">
                <i className="ri-map-pin-line" style={{ color: '#ff2d78' }} />
                {pharmacy.address}
              </p>

              {/* Rating */}
              <div className="flex items-center gap-4 mt-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-0.5">
                    {[1,2,3,4,5].map(i => (
                      <i key={i} className={i <= Math.round(pharmacy.rating) ? 'ri-star-fill' : 'ri-star-line'} style={{ color: '#f0ff00', fontSize: '16px' }} />
                    ))}
                  </div>
                  <span className="font-orbitron font-bold text-lg" style={{ color: '#f0ff00' }}>{pharmacy.rating}</span>
                  <span className="font-mono text-xs text-white/35">({pharmacy.reviews} отзывов)</span>
                </div>
                <div className="flex items-center gap-1.5 font-mono text-xs text-white/40">
                  <i className="ri-walk-line" />
                  {pharmacy.distance}
                </div>
                {/* Opening hours inline */}
                <div className="flex items-center gap-1.5 font-mono text-xs" style={{ color: 'rgba(0,245,255,0.7)' }}>
                  <i className="ri-time-line" />
                  <time>{openingHoursText}</time>
                </div>
              </div>

              {/* Service badges */}
              <div className="flex flex-wrap gap-2 mt-4">
                {PHARMACY_SERVICES.filter(s => pharmacy.services.includes(s.id)).map(svc => (
                  <span
                    key={svc.id}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full font-mono text-[10px]"
                    style={{ background: `${svc.color}10`, border: `1px solid ${svc.color}25`, color: svc.color }}
                  >
                    <i className={`${svc.icon} text-[10px]`} />
                    {svc.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:min-w-[200px]">
              <a
                href={`tel:${pharmacy.phone.replace(/\s/g, '')}`}
                className="whitespace-nowrap flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-mono text-sm cursor-pointer transition-all duration-200"
                style={{ background: 'rgba(255,45,120,0.15)', border: '1px solid rgba(255,45,120,0.4)', color: '#ff2d78' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,45,120,0.25)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,45,120,0.15)'; }}
              >
                <i className="ri-phone-line" />
                {pharmacy.phone}
              </a>
              <button
                onClick={handleCopyPhone}
                className="whitespace-nowrap flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-mono text-sm cursor-pointer transition-all duration-200"
                style={{ border: '1px solid rgba(255,255,255,0.1)', color: copied ? '#39ff14' : 'rgba(255,255,255,0.4)' }}
              >
                <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'} />
                {copied ? 'Скопировано!' : 'Скопировать номер'}
              </button>
              <Link
                to="/catalog"
                className="whitespace-nowrap flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-mono text-sm cursor-pointer transition-all duration-200"
                style={{ background: 'rgba(0,245,255,0.08)', border: '1px solid rgba(0,245,255,0.25)', color: '#00f5ff' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(0,245,255,0.15)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(0,245,255,0.08)'; }}
              >
                <i className="ri-medicine-bottle-line" />
                Заказать лекарства
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main content */}
      <section className="pb-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col xl:flex-row gap-8">

            {/* Left: Gallery + Tabs */}
            <div className="flex-1 min-w-0">
              {/* Gallery */}
              <PharmacyGallery images={pharmacy.gallery} name={pharmacy.name} />

              {/* Tabs */}
              <div className="mt-8">
                <div
                  className="flex gap-1 p-1 rounded-2xl mb-6 overflow-x-auto"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
                >
                  {tabs.map(t => (
                    <button
                      key={t.id}
                      onClick={() => setTab(t.id)}
                      className="whitespace-nowrap flex items-center gap-2 px-4 py-2.5 rounded-xl font-mono text-sm cursor-pointer transition-all duration-200 flex-1 justify-center"
                      style={tab === t.id
                        ? { background: 'rgba(255,45,120,0.15)', border: '1px solid rgba(255,45,120,0.35)', color: '#ff2d78' }
                        : { color: 'rgba(255,255,255,0.35)' }
                      }
                    >
                      <i className={t.icon} />
                      <span className="hidden sm:inline">{t.label}</span>
                    </button>
                  ))}
                </div>

                {/* Tab: Info */}
                {tab === 'info' && (
                  <div className="space-y-5" style={{ animation: 'fadeInUp 0.3s ease' }}>
                    <div className="p-5 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                      <h2 className="font-orbitron font-bold text-sm text-white mb-3 flex items-center gap-2">
                        <i className="ri-information-line" style={{ color: '#ff2d78' }} />
                        Об аптеке
                      </h2>
                      <p className="font-mono text-sm text-white/55 leading-relaxed">{pharmacy.description}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {pharmacy.features.map(f => (
                        <div
                          key={f.label}
                          className="flex items-center gap-3 p-4 rounded-2xl"
                          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
                        >
                          <div className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0" style={{ background: 'rgba(255,45,120,0.1)', border: '1px solid rgba(255,45,120,0.2)' }}>
                            <i className={f.icon} style={{ color: '#ff2d78', fontSize: '16px' }} />
                          </div>
                          <div>
                            <div className="font-mono text-[10px] text-white/35 uppercase tracking-wider">{f.label}</div>
                            <div className="font-mono text-sm text-white/80 mt-0.5">{f.value}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="p-5 rounded-2xl" style={{ background: 'rgba(0,245,255,0.03)', border: '1px solid rgba(0,245,255,0.12)' }}>
                      <h3 className="font-orbitron font-bold text-sm text-white mb-3 flex items-center gap-2">
                        <i className="ri-route-line" style={{ color: '#00f5ff' }} />
                        Как добраться
                      </h3>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-5 h-5 flex items-center justify-center rounded-full shrink-0" style={{ background: pharmacy.metroColor }}>
                          <span className="text-white font-bold" style={{ fontSize: '10px' }}>М</span>
                        </div>
                        <div>
                          <span className="font-mono text-sm text-white/80">{pharmacy.metro}</span>
                          <span className="font-mono text-xs text-white/35 ml-2">— {pharmacy.metroLine}</span>
                        </div>
                      </div>
                      <p className="font-mono text-sm text-white/55 leading-relaxed">{pharmacy.howToGet}</p>
                    </div>

                    <div className="p-5 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                      <h3 className="font-orbitron font-bold text-sm text-white mb-3 flex items-center gap-2">
                        <i className="ri-bank-card-line" style={{ color: '#f0ff00' }} />
                        Способы оплаты
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {pharmacy.paymentMethods.map(pm => (
                          <span
                            key={pm}
                            className="px-3 py-1.5 rounded-xl font-mono text-xs"
                            style={{ background: 'rgba(240,255,0,0.05)', border: '1px solid rgba(240,255,0,0.15)', color: 'rgba(240,255,0,0.7)' }}
                          >
                            {pm}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* FAQ section — visible to users, also powers FAQPage schema */}
                    <div className="p-5 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                      <h3 className="font-orbitron font-bold text-sm text-white mb-4 flex items-center gap-2">
                        <i className="ri-question-answer-line" style={{ color: '#c77dff' }} />
                        Часто задаваемые вопросы
                      </h3>
                      <div className="space-y-4">
                        {[
                          {
                            q: `Как добраться до аптеки ${pharmacy.name}?`,
                            a: pharmacy.howToGet,
                          },
                          {
                            q: `Какой режим работы у аптеки?`,
                            a: pharmacy.is24h
                              ? 'Аптека работает круглосуточно, 24 часа в сутки, 7 дней в неделю, без выходных.'
                              : `Аптека работает ${pharmacy.hours}. Расписание по дням: ${pharmacy.schedule.map(s => `${s.day}: ${s.open}–${s.close}`).join(', ')}.`,
                          },
                          {
                            q: 'Есть ли доставка из аптеки?',
                            a: pharmacy.hasDelivery
                              ? 'Да, аптека осуществляет доставку лекарств на дом.'
                              : 'Доставка из этой аптеки недоступна. Вы можете заказать доставку через сайт NeonPharma.',
                          },
                        ].map(({ q, a }) => (
                          <div key={q} className="border-b pb-4 last:border-b-0 last:pb-0" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                            <div className="font-mono text-sm text-white/80 mb-1.5 flex items-start gap-2">
                              <i className="ri-question-line shrink-0 mt-0.5" style={{ color: '#c77dff' }} />
                              {q}
                            </div>
                            <p className="font-mono text-xs text-white/45 leading-relaxed pl-5">{a}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Tab: Schedule */}
                {tab === 'schedule' && (
                  <div style={{ animation: 'fadeInUp 0.3s ease' }}>
                    <div className="p-5 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                      {pharmacy.is24h ? (
                        <div className="flex flex-col items-center py-8 gap-4">
                          <div className="w-16 h-16 flex items-center justify-center rounded-2xl" style={{ background: 'rgba(57,255,20,0.1)', border: '1px solid rgba(57,255,20,0.3)' }}>
                            <i className="ri-24-hours-line text-3xl" style={{ color: '#39ff14' }} />
                          </div>
                          <div className="text-center">
                            <div className="font-orbitron font-black text-2xl" style={{ color: '#39ff14', textShadow: '0 0 20px rgba(57,255,20,0.3)' }}>Работает 24/7</div>
                            <div className="font-mono text-sm text-white/40 mt-1">Без выходных и праздников</div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {pharmacy.schedule.map(s => (
                            <div
                              key={s.day}
                              className="flex items-center justify-between px-4 py-3 rounded-xl transition-all"
                              style={s.isToday
                                ? { background: 'rgba(255,45,120,0.08)', border: '1px solid rgba(255,45,120,0.25)' }
                                : { border: '1px solid transparent' }
                              }
                            >
                              <div className="flex items-center gap-3">
                                {s.isToday && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: '#ff2d78' }} />}
                                <span className="font-mono text-sm w-8" style={{ color: s.isToday ? '#ff2d78' : 'rgba(255,255,255,0.5)' }}>
                                  {s.day}
                                </span>
                                {s.isToday && (
                                  <span className="font-mono text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,45,120,0.15)', color: '#ff2d78' }}>
                                    Сегодня
                                  </span>
                                )}
                              </div>
                              <time className="font-mono text-sm" style={{ color: s.isToday ? 'white' : 'rgba(255,255,255,0.6)' }}>
                                {s.open} — {s.close}
                              </time>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="mt-4 flex items-center gap-3 p-4 rounded-2xl" style={{ background: 'rgba(0,245,255,0.04)', border: '1px solid rgba(0,245,255,0.15)' }}>
                      <i className="ri-time-line text-lg" style={{ color: '#00f5ff' }} />
                      <div>
                        <div className="font-mono text-xs text-white/40">Время работы</div>
                        <div className="font-mono text-sm text-white/80">{pharmacy.hours}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tab: Services */}
                {tab === 'services' && (
                  <div style={{ animation: 'fadeInUp 0.3s ease' }}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {PHARMACY_SERVICES.map(svc => {
                        const active = pharmacy.services.includes(svc.id);
                        return (
                          <div
                            key={svc.id}
                            className="flex items-center gap-4 p-4 rounded-2xl"
                            style={active
                              ? { background: `${svc.color}08`, border: `1px solid ${svc.color}25` }
                              : { background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.05)', opacity: 0.4 }
                            }
                          >
                            <div className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0" style={{ background: active ? `${svc.color}15` : 'rgba(255,255,255,0.04)', border: `1px solid ${active ? svc.color + '30' : 'rgba(255,255,255,0.08)'}` }}>
                              <i className={svc.icon} style={{ color: active ? svc.color : 'rgba(255,255,255,0.25)', fontSize: '18px' }} />
                            </div>
                            <div className="flex-1">
                              <div className="font-mono text-sm" style={{ color: active ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)' }}>{svc.label}</div>
                              <div className="font-mono text-xs mt-0.5" style={{ color: active ? `${svc.color}80` : 'rgba(255,255,255,0.2)' }}>{active ? 'Доступно' : 'Недоступно'}</div>
                            </div>
                            <div className="w-5 h-5 flex items-center justify-center rounded-full shrink-0" style={{ background: active ? `${svc.color}20` : 'rgba(255,255,255,0.04)' }}>
                              <i className={active ? 'ri-check-line' : 'ri-close-line'} style={{ color: active ? svc.color : 'rgba(255,255,255,0.2)', fontSize: '12px' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Tab: Reviews */}
                {tab === 'reviews' && (
                  <div style={{ animation: 'fadeInUp 0.3s ease' }}>
                    <PharmacyReviews reviews={pharmacy.reviewsList} rating={pharmacy.rating} totalCount={pharmacy.reviews} />
                  </div>
                )}
              </div>
            </div>

            {/* Right sidebar */}
            <div className="xl:w-[340px] shrink-0 space-y-5">
              {/* Map */}
              <div>
                <h3 className="font-orbitron font-bold text-sm text-white mb-3 flex items-center gap-2">
                  <i className="ri-map-2-line" style={{ color: '#00f5ff' }} />
                  На карте
                </h3>
                <div className="relative rounded-2xl overflow-hidden" style={{ height: '260px', border: '1px solid rgba(0,245,255,0.2)' }}>
                  {['top-0 left-0', 'top-0 right-0', 'bottom-0 left-0', 'bottom-0 right-0'].map((pos, i) => (
                    <div key={i} className={`absolute ${pos} w-5 h-5 z-10 pointer-events-none`}>
                      <div className="absolute inset-0" style={{
                        borderTop: i < 2 ? '1px solid #00f5ff' : 'none',
                        borderBottom: i >= 2 ? '1px solid #00f5ff' : 'none',
                        borderLeft: i % 2 === 0 ? '1px solid #00f5ff' : 'none',
                        borderRight: i % 2 === 1 ? '1px solid #00f5ff' : 'none',
                      }} />
                    </div>
                  ))}
                  <iframe
                    src={pharmacy.mapEmbedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) saturate(0.7) brightness(0.85)' }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={`Карта — ${pharmacy.name}`}
                  />
                </div>
                <a
                  href={`https://maps.google.com/?q=${pharmacy.coords.lat},${pharmacy.coords.lng}`}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="whitespace-nowrap mt-2 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-mono text-xs cursor-pointer transition-all duration-200"
                  style={{ border: '1px solid rgba(0,245,255,0.2)', color: '#00f5ff' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(0,245,255,0.06)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <i className="ri-external-link-line" />
                  Открыть в Google Maps
                </a>
              </div>

              {/* Quick info card */}
              <div className="p-5 rounded-2xl space-y-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <h3 className="font-orbitron font-bold text-sm text-white">Контакты</h3>
                {[
                  { icon: 'ri-map-pin-line', label: 'Адрес', value: pharmacy.address, color: '#ff2d78' },
                  { icon: 'ri-phone-line', label: 'Телефон', value: pharmacy.phone, color: '#39ff14' },
                  { icon: 'ri-time-line', label: 'Режим работы', value: pharmacy.hours, color: '#00f5ff' },
                  { icon: 'ri-map-2-line', label: 'Метро', value: pharmacy.metro, color: '#f0ff00' },
                ].map(item => (
                  <div key={item.label} className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0 mt-0.5" style={{ background: `${item.color}10`, border: `1px solid ${item.color}20` }}>
                      <i className={item.icon} style={{ color: item.color, fontSize: '14px' }} />
                    </div>
                    <div>
                      <div className="font-mono text-[10px] text-white/30 uppercase tracking-wider">{item.label}</div>
                      <div className="font-mono text-sm text-white/70 mt-0.5 leading-snug">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Metro exit */}
              <div className="p-4 rounded-2xl" style={{ background: 'rgba(0,245,255,0.03)', border: '1px solid rgba(0,245,255,0.12)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full shrink-0" style={{ background: pharmacy.metroColor }}>
                    <span className="text-white font-bold" style={{ fontSize: '10px' }}>М</span>
                  </div>
                  <span className="font-mono text-xs text-white/60">{pharmacy.metro}</span>
                </div>
                <p className="font-mono text-xs text-white/45 leading-relaxed">{pharmacy.metroExit}</p>
              </div>

              {/* Nearby pharmacies */}
              {nearby.length > 0 && (
                <div>
                  <h3 className="font-orbitron font-bold text-sm text-white mb-3 flex items-center gap-2">
                    <i className="ri-store-2-line" style={{ color: '#ff2d78' }} />
                    Ближайшие аптеки
                  </h3>
                  <div className="space-y-2">
                    {nearby.map(n => (
                      <Link
                        key={n.id}
                        to={`/pharmacies/${n.id}`}
                        className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 group"
                        style={{ border: '1px solid rgba(255,255,255,0.07)' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,45,120,0.25)'; (e.currentTarget as HTMLElement).style.background = 'rgba(255,45,120,0.04)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.07)'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                      >
                        <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0">
                          <img src={n.image} alt={n.name} className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-xs text-white/70 truncate group-hover:text-white transition-colors">{n.name}</div>
                          <div className="font-mono text-[10px] text-white/35 truncate mt-0.5">{n.address}</div>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <div className="flex items-center gap-0.5">
                            <i className="ri-star-fill text-[10px]" style={{ color: '#f0ff00' }} />
                            <span className="font-mono text-[10px] text-white/50">{n.rating}</span>
                          </div>
                          <div className="w-2 h-2 rounded-full" style={{ background: n.isOpen || n.is24h ? '#39ff14' : '#ff2d78' }} />
                        </div>
                      </Link>
                    ))}
                  </div>
                  <Link
                    to="/pharmacies"
                    className="whitespace-nowrap mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-mono text-xs cursor-pointer transition-all duration-200"
                    style={{ border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.35)' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,45,120,0.3)'; (e.currentTarget as HTMLElement).style.color = '#ff2d78'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.35)'; }}
                  >
                    <i className="ri-map-2-line" />
                    Все аптеки на карте
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default PharmacyDetailPage;
